<template>
  <section class="workspace-stack">
    <article class="glass-surface workspace-panel">
      <div class="workspace-panel__header">
        <div>
          <span class="workspace-kicker">Transferencias pendientes</span>
          <h2 class="workspace-title">Validación de comprobantes</h2>
          <p class="workspace-copy">Revisa capturas, filtra la cola y define el estado final de cada pago manual.</p>
        </div>
      </div>

      <div class="workspace-metrics approval-metrics">
        <article class="workspace-metric">
          <small>Pendientes</small>
          <strong>{{ filteredEvents.length }}</strong>
          <p>Pagos visibles con el filtro actual.</p>
        </article>
        <article class="workspace-metric">
          <small>Monto en revisión</small>
          <strong>${{ pendingTotal }}</strong>
          <p>Total acumulado en la bandeja.</p>
        </article>
        <article class="workspace-metric">
          <small>Automáticos</small>
          <strong>{{ automaticCount }}</strong>
          <p>Casos que pasaron la validación automática.</p>
        </article>
        <article class="workspace-metric">
          <small>Manual</small>
          <strong>{{ manualCount }}</strong>
          <p>Casos que requieren decisión humana.</p>
        </article>
      </div>

      <div class="workspace-toolbar approval-toolbar">
        <label class="workspace-search approval-toolbar__search">
          <i class="fas fa-search"></i>
          <input v-model.trim="search" type="text" placeholder="Buscar por cliente, comprobante o evento" />
        </label>

        <div class="workspace-segmented-control approval-toolbar__filters">
          <button
            v-for="filter in analysisFilters"
            :key="filter.value"
            :class="['workspace-segmented-control__item', { 'is-active': analysisFilter === filter.value }]"
            type="button"
            @click="analysisFilter = filter.value"
          >
            <span>{{ filter.label }}</span>
          </button>
        </div>
      </div>

      <div v-if="feedback" :class="['workspace-feedback', feedbackType === 'error' ? 'workspace-feedback--error' : 'workspace-feedback--success']">{{ feedback }}</div>

      <div v-if="!filteredEvents.length" class="workspace-empty">No hay transferencias pendientes para ese filtro.</div>
      <div v-else class="workspace-table-shell">
        <table class="workspace-table approval-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Fecha</th>
              <th>Cliente</th>
              <th>Comprobante</th>
              <th>Evento</th>
              <th>Captura</th>
              <th>Monto</th>
              <th>Análisis</th>
              <th>Nuevo estado</th>
              <th>Acción</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="payment in filteredEvents" :key="payment.id">
              <td data-label="ID">#{{ payment.id }}</td>
              <td data-label="Fecha">{{ formatDate(payment.date) }}</td>
              <td data-label="Cliente">
                <div class="approval-cell-stack">
                  <strong>{{ payment.customer || 'Sin nombre' }}</strong>
                  <small>{{ payment.email || payment.phone || '' }}</small>
                </div>
              </td>
              <td data-label="Comprobante">{{ payment.url_number || '-' }}</td>
              <td data-label="Evento">{{ payment.event || '-' }}</td>
              <td data-label="Captura">
                <a v-if="payment.file" class="workspace-inline-link" :href="payment.file" target="_blank">Ver imagen</a>
                <span v-else>-</span>
              </td>
              <td data-label="Monto">${{ formatMoney(payment.amount) }}</td>
              <td data-label="Analisis">
                <span :class="['workspace-status-pill', payment.automatic_validation ? 'is-active' : 'is-warning']">
                  {{ payment.automatic_validation ? 'Validación automática' : 'Revisión manual' }}
                </span>
              </td>
              <td data-label="Nuevo estado">
                <select class="workspace-input workspace-select" :value="selectedStatuses[payment.id] || ''" @change="setPaymentStatus(payment, $event)">
                  <option value="" disabled>Estado</option>
                  <option value="1">Aprobado</option>
                  <option value="2">Pago falso</option>
                  <option value="3">Pago duplicado</option>
                  <option value="6">Transferido</option>
                </select>
              </td>
              <td data-label="Accion">
                <button class="workspace-button workspace-button--primary workspace-button--small" type="button" @click="changeStatusByPayment(payment)">
                  <i class="fas fa-check"></i>
                  <span>Confirmar</span>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="workspace-actions workspace-actions--end">
        <spring-spinner v-if="loading" v-show="next" :size="48" color="#8cf3e8" />
        <button v-show="next && !loading" class="workspace-button" type="button" @click="getEvents">Cargar mas</button>
      </div>
    </article>
  </section>
</template>

<script>
import { apiService } from '../../common/api.services.js'
import { SpringSpinner } from 'epic-spinners'

export default {
  name: 'AssistanceList',

  components: {
    SpringSpinner,
  },

  props: {
    event: {
      required: false,
      default: null,
    },
  },

  data() {
    return {
      events: [],
      next: null,
      search: '',
      loading: false,
      analysisFilter: 'all',
      selectedStatuses: {},
      feedback: '',
      feedbackType: 'success',
    }
  },

  computed: {
    analysisFilters() {
      return [
        { value: 'all', label: 'Todos' },
        { value: 'automatic', label: 'Automático' },
        { value: 'manual', label: 'Manual' },
      ]
    },

    filteredEvents() {
      const term = this.search.trim().toLowerCase()

      return this.events.filter((payment) => {
        if (this.analysisFilter === 'automatic' && !payment.automatic_validation) {
          return false
        }

        if (this.analysisFilter === 'manual' && payment.automatic_validation) {
          return false
        }

        if (!term) {
          return true
        }

        const haystack = `${payment.customer || ''} ${payment.url_number || ''} ${payment.event || ''}`.toLowerCase()
        return haystack.includes(term)
      })
    },

    pendingTotal() {
      return this.filteredEvents.reduce((sum, payment) => sum + (Number.parseFloat(payment.amount) || 0), 0).toFixed(2)
    },

    automaticCount() {
      return this.events.filter((payment) => payment.automatic_validation).length
    },

    manualCount() {
      return this.events.filter((payment) => !payment.automatic_validation).length
    },
  },

  created() {
    this.getEvents()
  },

  methods: {
    setPaymentStatus(payment, event) {
      this.selectedStatuses = {
        ...this.selectedStatuses,
        [payment.id]: event.target.value,
      }
    },

    formatMoney(value) {
      const parsed = Number.parseFloat(value)
      return Number.isNaN(parsed) ? '0.00' : parsed.toFixed(2)
    },

    formatDate(dateString) {
      if (!dateString) {
        return 'Sin fecha'
      }

      return new Intl.DateTimeFormat('default', { dateStyle: 'short' }).format(new Date(dateString))
    },

    changeStatus(index) {
      const payment = this.events[index]
      const nextStatus = this.selectedStatuses[payment.id]
      if (!nextStatus) {
        return
      }

      this.feedback = ''
      apiService(`/api/payment/${payment.id}/change?type=${nextStatus}`, 'PUT').then((data) => {
        if (data && data.id) {
          this.events.splice(index, 1)
          const clone = { ...this.selectedStatuses }
          delete clone[payment.id]
          this.selectedStatuses = clone
          this.feedbackType = 'success'
          this.feedback = `Pago #${payment.id} actualizado correctamente.`
        } else {
          this.feedbackType = 'error'
          this.feedback = (data && data.err) || 'No pudimos actualizar el pago.'
        }
      }).catch(() => {
        this.feedbackType = 'error'
        this.feedback = 'No pudimos actualizar el pago.'
      })
    },

    changeStatusByPayment(payment) {
      const index = this.events.findIndex((item) => item.id === payment.id)
      if (index === -1 || !this.selectedStatuses[payment.id]) {
        return
      }

      this.changeStatus(index)
    },

    getEvents() {
      let endpoint = '/api/payments/?status=pending'

      if (this.next) {
        endpoint = this.next
      }

      this.loading = true
      apiService(endpoint).then((data) => {
        const items = Array.isArray(data) ? data : (data && data.results ? data.results : [])
        this.events = this.events.concat(items)
        this.next = data && data.next ? data.next : null
      }).finally(() => {
        this.loading = false
      })
    },
  },
}
</script>

<style scoped>
.approval-metrics {
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
}

.approval-toolbar {
  display: grid;
  gap: 12px;
}

.approval-toolbar__search {
  width: 100%;
}

.approval-toolbar__filters {
  flex-wrap: wrap;
}

.approval-table {
  min-width: 1100px;
}

.approval-cell-stack {
  display: grid;
  gap: 2px;
}

.approval-cell-stack small {
  color: rgba(208, 219, 238, 0.7);
}

@media (max-width: 900px) {
  .approval-table thead {
    display: none;
  }

  .approval-table,
  .approval-table tbody,
  .approval-table tr,
  .approval-table td {
    display: block;
    width: 100%;
  }

  .approval-table {
    min-width: 0;
    border: none;
    background: transparent;
  }

  .approval-table tr {
    margin-bottom: 14px;
    padding: 14px;
    border-radius: 18px;
    border: 1px solid rgba(148, 163, 184, 0.18);
    background: rgba(8, 13, 24, 0.96);
  }

  .approval-table td {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    padding: 10px 0;
    border-bottom: 1px solid rgba(223, 214, 255, 0.08);
  }

  .approval-table td:last-child {
    border-bottom: none;
  }

  .approval-table td::before {
    content: attr(data-label);
    font-size: 0.72rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: rgba(208, 219, 238, 0.68);
    font-weight: 700;
  }
}
</style>
